import * as Sentry from '@sentry/react'
import axios from 'axios'
import { signOut as firebaseSignOut, getIdToken, onAuthStateChanged, signInWithEmailAndPassword, signInWithPopup, User } from 'firebase/auth'
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useLogoutAccount } from '../../hooks/api/auth/useLogoutAccount'
import { useVerifyAccount } from '../../hooks/api/auth/useVerifyAccount'
import { t } from '../../i18n'
import { AuthVerifyApiResponse } from '../../types/interfaces'
import { AuthContext } from '../context/authContext'
import { appleProvider, auth, provider } from '../firebase/config'
import { useLocalStorage } from '../hooks/useLocalStorage'
import routes from '../router/routes'
import { UserStatusType, UserType } from '../types/interface'
import { getAuthToken } from '../utils/auth-token'

// Local type definitions (previously from aws-amplify/auth)
type SignInInput = { password: string; username: string }
type SignInOutput = { isSignedIn: boolean; nextStep: { signInStep: string } }
type JWT = string

const initialLanding = '/portal/home'
const initialUserStatus: UserStatusType = { status: '', tz: '' }

// Map Firebase error codes to translation keys
const getFirebaseErrorMessage = (errorCode: string): string => {
  const translationKeyMap: Record<string, string> = {
    'auth/invalid-credential': 'auth.signin.errors.invalid_credentials',
    'auth/invalid-email': 'auth.signin.errors.invalid_email_format',
    'auth/network-request-failed': 'auth.signin.errors.network_error',
    'auth/too-many-requests': 'auth.signin.errors.too_many_requests',
    'auth/user-disabled': 'auth.signin.errors.account_disabled',
    'auth/user-not-found': 'auth.signin.errors.invalid_credentials',
    'auth/wrong-password': 'auth.signin.errors.invalid_credentials',
  }

  const translationKey = translationKeyMap[errorCode]
  return translationKey ? t(translationKey) : t('auth.signin.errors.generic')
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [currentUser, setCurrentUser] = useLocalStorage<unknown>('currentUser', null)
  const [isAuthLoading, setIsAuthLoading] = useState(true)
  const [isOnboarded, setIsOnboarded] = useLocalStorage('onboard', false)
  const [onboardUserData, setOnboardUserData] = useState<AuthVerifyApiResponse | null>(null)
  const [userInfo, setUserInfo] = useState<null | UserType>(null)
  const [userStatus, setUserStatus] = useState<UserStatusType>(initialUserStatus)

  const navigate = useNavigate()
  const { isPending: isVerifyingAccount, mutateAsync: verifyAccount } = useVerifyAccount()
  const { mutateAsync: logoutAccount } = useLogoutAccount()

  const getAccessJwtToken = async (): Promise<JWT | undefined> => {
    const token = await getAuthToken()
    return token as JWT
  }

  const registerDriverApiUser = async (values: unknown, firebaseToken: string) => {
    await axios.post(`${import.meta.env.VITE_DRIVER_API_BASE_URL}/v2/auth/register`, values, {
      headers: { Authorization: firebaseToken },
    })
  }

  const parseDisplayName = (displayName: string) => {
    const parts = displayName.split(' ')
    return { firstName: parts[0] || '', lastName: parts.slice(1).join(' ') || '' }
  }

  const setupFirebaseUser = useCallback(async (
    firebaseUser: User,
    currentUserData: AuthVerifyApiResponse
  ) => {
    const displayName = firebaseUser.displayName || ''
    const { firstName, lastName } = parseDisplayName(displayName)
    const email = firebaseUser.email || ''

    setUserInfo({ email, firstName, lastName })
    setCurrentUser(firebaseUser)
    setOnboardUserData(currentUserData)
    setIsOnboarded(currentUserData.onBoarded || false)

    Sentry.setUser({
      email,
      id: currentUserData.userId,
      ip_address: '{{auto}}',
      name: displayName || email,
    })
  }, [setCurrentUser, setIsOnboarded, setUserInfo, setOnboardUserData])

  const getFirebaseError = (error: unknown): string => {
    const firebaseError = error as { code?: string; message?: string }
    if (firebaseError.code) {
      return getFirebaseErrorMessage(firebaseError.code)
    }
    return firebaseError.message || t('auth.signin.errors.generic')
  }

  const handleGoogleSSO = useCallback(async () => {
      const result = await signInWithPopup(auth, provider)
      const { user } = result
      const accessToken = await user.getIdToken()
      
      const response = await axios.get(`${import.meta.env.VITE_DRIVER_API_BASE_URL}/v2/auth/sso`, {
        headers: { Authorization: accessToken },
      })

      if (response.data.registration_required) {
        const { firstName, lastName } = parseDisplayName(user.displayName || '')
        await registerDriverApiUser({
          email: user.email || '',
          first_name: firstName,
          last_name: lastName,
        }, accessToken)
      }

      const currentUserData = await verifyAccount({
        email: user.email || '',
        provider: 'external',
      })

      await setupFirebaseUser(user, currentUserData)
  }, [verifyAccount, setupFirebaseUser])

  const handleAppleSSO = useCallback(async () => {
    const result = await signInWithPopup(auth, appleProvider)
    const { user } = result
    const accessToken = await user.getIdToken()
    
    const response = await axios.get(`${import.meta.env.VITE_DRIVER_API_BASE_URL}/v2/auth/sso`, {
      headers: { Authorization: accessToken },
    })

    if (response.data.registration_required) {
      const { firstName, lastName } = parseDisplayName(user.displayName || '')
      await registerDriverApiUser({
        email: user.email || '',
        first_name: firstName,
        last_name: lastName,
      }, accessToken)
    }

    const currentUserData = await verifyAccount({
      email: user.email || '',
      provider: 'external',
    })

    await setupFirebaseUser(user, currentUserData)
  }, [verifyAccount, setupFirebaseUser])

  const signInWithFirebase = useCallback(async (
    email: string,
    password: string
  ): Promise<SignInOutput> => {
    try {
      const { user: firebaseUser } = await signInWithEmailAndPassword(auth, email, password)
      await getIdToken(firebaseUser)

      const currentUserData = await verifyAccount({
        email: firebaseUser.email || email,
        provider: 'external',
      })

      await setupFirebaseUser(firebaseUser, currentUserData)

      return {
        isSignedIn: true,
        nextStep: { signInStep: 'DONE' },
      } as SignInOutput
    } catch (error) {
      throw new Error(getFirebaseError(error))
    }
  }, [verifyAccount, setupFirebaseUser])

  const signIn = useCallback(async ({ password, username }: SignInInput): Promise<SignInOutput> => {
    if (!username || !password) {
      throw new Error('Username and password are required')
    }

    try {
      const response = await axios.get(`${import.meta.env.VITE_DRIVER_API_BASE_URL}/v2/auth/validate-pre-signin`, {
        params: { email: username },
      })

      if (response.data.migrated) {
        return {
          isSignedIn: false,
          nextStep: { signInStep: 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED' },
        }
      }

      if (response.data.auth_provider === 'firebase') {
        return signInWithFirebase(username, password)
      }

      // Only Firebase is supported
      throw new Error('Authentication provider not supported. Please use Firebase authentication.')
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          throw new Error('Invalid email or password')
        }
        throw new Error(error.response?.data?.message || 'Unable to verify credentials. Please try again')
      }
      throw error
    }
  }, [signInWithFirebase])
  

  const clearUserData = useCallback(() => {
    setCurrentUser(null)
    setUserStatus(initialUserStatus)
    setIsOnboarded(false)
    setOnboardUserData(null)
    localStorage.clear()
  }, [setCurrentUser, setIsOnboarded])

  const handleSignOut = useCallback(async () => {
    try {
      if (currentUser) await logoutAccount()
      
      await firebaseSignOut(auth)
      
      clearUserData()
      Sentry.setUser(null)
    } catch (error) {
      Sentry.captureException(error)
      console.log(error)
    }
  }, [currentUser, logoutAccount, clearUserData])


  const getCurrentUserData = useCallback(async () => {
    if (!onboardUserData) {
      throw new Error('No user data available')
    }
    return onboardUserData
  }, [onboardUserData])

  const checkAuthentication = useCallback(() => {
    if (!currentUser) {
      navigate(routes.auth)
      return
    }
    navigate(isOnboarded ? routes.portal.home : routes.onboard)
  }, [isOnboarded, navigate, currentUser])



  const getOnboardData = useCallback(async () => {
    try {
      setOnboardUserData(await getCurrentUserData())
    } catch (error: unknown) {
      Sentry.captureException(error)
      console.error(error)
    }
  }, [getCurrentUserData])


  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (!firebaseUser) {
        clearUserData()
      }
      setIsAuthLoading(false)
    })
    return unsubscribe
  // clearUserData is memoized with stable deps — safe to omit from array
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (currentUser && !isVerifyingAccount && !onboardUserData) {
      getOnboardData()
    }
  }, [currentUser, onboardUserData, isVerifyingAccount, getOnboardData])



  const value = useMemo(
    () => ({
      checkAuthentication,
      clearUserData,
      getAccessJwtToken,
      getOnboardData,
      handleAppleSSO,
      handleGoogleSSO,
      initialLanding,
      isAuthLoading,
      isOnboarded,
      onboardUserData,
      setIsOnboarded,
      setUserStatus,
      signIn,
      signOut: handleSignOut,
      user: currentUser,
      userInfo,
      userStatus,
    }),
    [
      checkAuthentication,
      clearUserData,
      getOnboardData,
      handleAppleSSO,
      handleGoogleSSO,
      isAuthLoading,
      isOnboarded,
      onboardUserData,
      setIsOnboarded,
      handleSignOut,
      signIn,
      currentUser,
      userInfo,
      userStatus,
    ]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
